import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Store, select } from '@ngrx/store';
import { IProduct } from 'src/app/products/models/product';
import { ProductService } from 'src/app/products/services/product.service';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { WearingPartStore } from 'src/app/stores/component/wearing-parts-store';
import * as WearingPartCategoryActions from "../../../stores/global/wearing-part-categories/actions";
import { ICreateWearingPartRequest, IGetWearingPartResponse, IUpdateWearingPartRequest } from '../../models/wearing-part';
import { BehaviorSubject, Observable, Subject, combineLatest, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs';
import { wearingPartCategoriesSelector } from 'src/app/stores/global/wearing-part-categories/selectors';
import { ActivatedRoute } from '@angular/router';
import { IBaseSearchRequest } from 'src/app/core/models/base';
import { ConnectivityStateService } from 'src/app/shared/services/connectivity-state.service';
import { ICreateBasketItemRequest, ICreateBasketRequest } from 'src/app/basket/models/basket';
import { BasketService } from 'src/app/basket/services/basket.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { CurrentBasketService } from 'src/app/basket/services/current-basket.service';
import { WearingPartService } from '../../services/wearing-part.service';
import { debug, RxJsLoggingLevel } from 'src/app/shared/rxjs-operators/debug-operator';

@Component({
    selector: 'app-device-detail-wearing-parts',
    templateUrl: './device-detail-wearing-parts.component.html',
    styleUrl: './device-detail-wearing-parts.component.scss',
    standalone: false
})
@UntilDestroy()
export class DeviceDetailWearingPartsComponent {
  @Input() isAdmin;
  @Input() companyOnDevice;

  deviceId: string;
  isNew: boolean;
  wearingPartOpened: boolean = false;

  editingWearingPart: IGetWearingPartResponse | null = null;
  public wearingPartForm: UntypedFormGroup;
  
  constructor(
    private store: Store<AppStateInterface>,
    private wearingPartStore: WearingPartStore,
    private wearingPartService: WearingPartService,
    private productService: ProductService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private basketService: BasketService,
    private appnotificationService: AppNotificationService,
    private connectivityStateService: ConnectivityStateService,
    private currentBasketService : CurrentBasketService
  ) {


  }

  public searchString$ = new Subject<string>();
  public productSearchIsLoading$ = new BehaviorSubject<boolean>(false);

  vm$ = combineLatest([ 
    this.store.pipe(select(wearingPartCategoriesSelector)),
    this.wearingPartStore.wearingParts$,
    this.wearingPartStore.loading$,
    this.connectivityStateService.connectionState$,
    this.currentBasketService.currentCompany$,
    this.searchString$.pipe(
      filter(x => x != null && x != ''),
      tap(x => this.productSearchIsLoading$.next(true)),
      debounceTime(500),
      switchMap(searchString => {
        const request: IBaseSearchRequest = {
          keyword: searchString,
          pageNumber: 1,
          pageSize: 25
        }
        return this.productService.listProducts(request)
      }),
      map(x => x.data.filter(p => p.isActive)),
      tap(x => this.productSearchIsLoading$.next(false)),
      startWith([])
    ),
    this.productSearchIsLoading$,
  ]).pipe(
    map(([
      wearingPartCategories, 
      wearingParts,
      loading,
      connectionState,
      currentCompany,
      searchResults,
      productSearchIsLoading
    ]) => ({
      wearingPartCategories,
      wearingParts,
      loading,
      connectionState,
      currentCompany,
      searchResults,
      productSearchIsLoading
    }))
  );

  ngOnInit(): void {
    this.initForm();

    this.route.paramMap.subscribe(params => {
      this.deviceId = params.get('id');
    });

    if (this.deviceId == "new") {
      this.isNew = true;
    } else {
      if (this.deviceId) {
        this.wearingPartStore.getWearingPartsByDevice(this.deviceId);
        this.store.dispatch(WearingPartCategoryActions.getWearingPartCategories());
      }
    }
  }

  initForm() {
    this.wearingPartForm = this.fb.group({
      wearingPartCategoryId: new UntypedFormControl (null, {
        validators: Validators.required
      }),
      productId: new UntypedFormControl (null, {
        validators: Validators.required
      }),
      quantity: new UntypedFormControl (1, {
        validators: Validators.required
      }),
    });
  }

  public openWearingPartDialog(): void {
    this.wearingPartOpened = true;
    this.editingWearingPart = null;
  }

  public closeWearingPartDialog(status: string): void {
    this.wearingPartOpened = false;
    this.wearingPartForm.reset();
  }

  editWearingPart(wearingPart: IGetWearingPartResponse): void {
    this.wearingPartOpened = true;
    
    this.editingWearingPart = wearingPart || null;

    if (wearingPart) {
      // Populate the form with existing note data for editing
      this.wearingPartForm.patchValue({
        wearingPartCategoryId: wearingPart.wearingPartCategory.id,
        productId: wearingPart.product.id,
        quantity: wearingPart.quantity,
      });

      this.onFilter(wearingPart.product.internalNumber);
    } else {
      // Reset form for creating a new note
      this.wearingPartForm.reset();
    }
  }

  removeWearingPart(): void {
    this.wearingPartStore.removeWearingPartById(this.editingWearingPart.id);
    this.closeWearingPartDialog('no');
  }

  submitWearingPartCreation() {
    if (this.editingWearingPart) {
      // Edit existing note
      const updateRequest: IUpdateWearingPartRequest = {
        id: this.editingWearingPart.id,
        wearingPartCategoryId: this.wearingPartForm.value.wearingPartCategoryId,
        deviceId: this.deviceId,
        quantity: this.wearingPartForm.value.quantity,
      };
      this.wearingPartStore.updateWearingPart(updateRequest);
    } else {
      // Create new note
      const request: ICreateWearingPartRequest = {
        wearingPartCategoryId: this.wearingPartForm.value.wearingPartCategoryId,
        productId: this.wearingPartForm.value.productId,
        deviceId: this.deviceId,
        quantity: this.wearingPartForm.value.quantity
      };
      this.wearingPartStore.addWearingPart(request);
    }
    this.closeWearingPartDialog('no');
  }

  // Fetch products based on search term
  onFilter(keywordInput: string): void {  
    this.searchString$.next(keywordInput);
    // this.productSearchIsLoading$.next(true);
  }

  // Adds a product with its quantity to
  addToCart(quantity: number, product: IProduct) {
    const request: ICreateBasketItemRequest = {
      quantity: quantity,
      productId: product.id,
      companyId: this.companyOnDevice
    }

    this.basketService.addBasketItem(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.appnotificationService.notification$.next({message:translate('basket.createSuccess'), style: "success"});
    });
  }

  exportWearingParts() {
    this.wearingPartService.exportWearingPartsByDevice(this.deviceId);
  }

  copyWearingParts(wearingParts: any[]) {
    if (navigator.clipboard) {

      const strippedData = wearingParts
        .filter(wearingPart => wearingPart.product.isActive)
        .map(wearingPart => ({
          sapNumber: wearingPart.product.internalNumber,
          quantity: wearingPart.quantity
        }));

      const rows = strippedData.map(part => 
        `${part.sapNumber}\t${part.quantity}`
      );
  
      const dataToCopy = rows.join('\n');

      navigator.clipboard.writeText(dataToCopy)
        .then(() => {
          this.appnotificationService.notification$.next({message: translate('wearingParts.copySucceeded'),style:'success'})
        })
        .catch(err => {
          this.appnotificationService.notification$.next({message: translate('wearingParts.copyFailed'),style:'success'})
        });
    } else {
      console.error('Clipboard API not supported in this browser.');
    }
  }
}
